import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import type { MemeAutocompleteItem } from './memeAutocompleteSource'
import mentionStyles from '../mentions.module.scss'

interface MemeSuggestionListRef {
    onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

interface MemeSuggestionListProps {
    items: MemeAutocompleteItem[]
    command: (item: MemeAutocompleteItem) => void
    editor: unknown
}

const MemeSuggestionList = forwardRef<MemeSuggestionListRef, MemeSuggestionListProps>(
    (props, ref) => {
        const [selectedIndex, setSelectedIndex] = useState(0)

        const selectItem = (index: number) => {
            const item = props.items[index]
            if (item) {
                props.command(item)
            }
        }

        const upHandler = () => {
            setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length)
        }

        const downHandler = () => {
            setSelectedIndex((selectedIndex + 1) % props.items.length)
        }

        const enterHandler = () => {
            selectItem(selectedIndex)
        }

        useEffect(() => setSelectedIndex(0), [props.items])

        useImperativeHandle(ref, () => ({
            onKeyDown: ({ event }: { event: KeyboardEvent }) => {
                if (!props.items.length) return false

                if (event.key === 'ArrowUp') {
                    upHandler()
                    return true
                }

                if (event.key === 'ArrowDown') {
                    downHandler()
                    return true
                }

                if (event.key === 'Enter' || event.key === 'Tab') {
                    enterHandler()
                    return true
                }

                return false
            },
        }))

        if (!props.items.length) return null

        return (
            <div className={mentionStyles.mentionList}>
                {props.items.map((item, index) => (
                    <div
                        className={`${mentionStyles.mentionItem} ${
                            index === selectedIndex ? mentionStyles.selected : ''
                        }`}
                        key={item.id || index}
                        onMouseDown={event => {
                            event.preventDefault()
                        }}
                        onClick={event => {
                            event.stopPropagation()
                            selectItem(index)
                        }}
                    >
                        <div className={mentionStyles.memeSuggestionItem}>
                            <span className={mentionStyles.memeSuggestionText}>
                                <span className={mentionStyles.memeSuggestionBracket}>[[</span>
                                {item.text}
                                <span className={mentionStyles.memeSuggestionBracket}>]]</span>
                            </span>
                            <span className={mentionStyles.memeSuggestionMeta}>
                                {item.thread_count} thread{item.thread_count === 1 ? '' : 's'}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        )
    },
)

MemeSuggestionList.displayName = 'MemeSuggestionList'

export default MemeSuggestionList
